import { useEffect, useState } from 'react';
import api from '../../api';

function NewsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image_url: '',
    display_order: 0
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await api.get('/api/admin/news');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch news:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        body: item.body || '',
        image_url: item.image_url || '',
        display_order: item.display_order ?? 0
      });
      setImageFile(null);
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        body: '',
        image_url: '',
        display_order: items.length
      });
      setImageFile(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('body', formData.body);
      fd.append('display_order', String(formData.display_order));
      if (editingItem && formData.image_url && !imageFile) {
        fd.append('image_url', formData.image_url);
      }
      if (imageFile) {
        fd.append('image', imageFile);
      }

      if (editingItem) {
        await api.put(`/api/admin/news/${editingItem.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/api/admin/news', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      closeModal();
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save news item');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem a certeza que pretende eliminar esta novidade?')) return;
    try {
      await api.delete(`/api/admin/news/${id}`);
      fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) {
    return <div className="loading">A carregar novidades...</div>;
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Novidades</h1>
        <button onClick={() => openModal()} className="btn btn-primary">+ Adicionar novidade</button>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
        Itens com imagem, título e texto. Aparecem na página pública <a href="/news" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>/news</a> (Novidades).
      </p>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Image</th>
              <th>Heading</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id}>
                <td>{item.display_order}</td>
                <td>
                  {item.image_url ? (
                    <img src={item.image_url} alt="" style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px' }} />
                  ) : (
                    <span style={{ color: 'var(--text-secondary)' }}>—</span>
                  )}
                </td>
                <td>{item.title}</td>
                <td>
                  <button onClick={() => openModal(item)} className="btn btn-small btn-secondary" style={{ marginRight: '5px' }}>Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="btn btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Ainda não há novidades. Clique em &quot;Adicionar novidade&quot; para criar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <h2>{editingItem ? 'Editar novidade' : 'Adicionar novidade'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Heading *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Text</label>
                <textarea
                  value={formData.body}
                  onChange={e => setFormData({ ...formData, body: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Image</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    setImageFile(f || null);
                  }}
                />
                {editingItem && formData.image_url && !imageFile && (
                  <div style={{ marginTop: '8px' }}>
                    <img src={formData.image_url} alt="" style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'cover', borderRadius: '4px' }} />
                    <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Current image (upload new to replace)</span>
                  </div>
                )}
                {imageFile && (
                  <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>New file: {imageFile.name}</span>
                )}
              </div>
              <div className="form-group">
                <label>Display order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Saving...' : (editingItem ? 'Save' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsManager;
